/**
 * Slash menu command registry for BB Docs editor.
 *
 * All commands are defined here as data. The getCustomSlashMenuItems function
 * in getCustomSlashMenuItems.ts converts these into BlockNote DefaultReactSuggestionItems.
 *
 * Groups:
 *  - "Blocks"     -- standard block insertions (heading, table, code, etc.)
 *  - "Media"      -- image upload, file embed
 *  - "Agents"     -- open agent chat in sidebar panel
 *  - "AI"         -- AI-powered text transformations
 *  - "StoryKeeper" -- beat save, frontmatter
 */

// ---- Types ----------------------------------------------------------------

export type CommandGroup = 'Blocks' | 'Media' | 'Agents' | 'AI' | 'StoryKeeper' | 'AI Media';

export interface SlashCommand {
  /** Unique key for the command. */
  key: string;
  /** Display title in the menu. */
  title: string;
  /** Short description shown below title. */
  subtext: string;
  /** Group header. */
  group: CommandGroup;
  /** Aliases that the user can type to filter. */
  aliases: string[];
  /** Emoji shown as icon fallback (JSX icon created at render time). */
  emoji: string;
  /** Optional badge text (e.g. "AI", "SK"). */
  badge?: string;
}

// ---- Block commands --------------------------------------------------------

export const blockCommands: SlashCommand[] = [
  {
    key: 'heading_1',
    title: 'Heading 1',
    subtext: 'Large section heading',
    group: 'Blocks',
    aliases: ['h1', 'heading1', 'kop1'],
    emoji: 'H',
  },
  {
    key: 'heading_2',
    title: 'Heading 2',
    subtext: 'Medium section heading',
    group: 'Blocks',
    aliases: ['h2', 'heading2', 'kop2'],
    emoji: 'H',
  },
  {
    key: 'heading_3',
    title: 'Heading 3',
    subtext: 'Small section heading',
    group: 'Blocks',
    aliases: ['h3', 'heading3', 'kop3'],
    emoji: 'H',
  },
  {
    key: 'table',
    title: 'Table',
    subtext: 'Insert a table',
    group: 'Blocks',
    aliases: ['table', 'tabel'],
    emoji: '#',
  },
  {
    key: 'code_block',
    title: 'Code Block',
    subtext: 'Insert a code block',
    group: 'Blocks',
    aliases: ['code', 'codeblock', 'pre'],
    emoji: '<>',
  },
  {
    key: 'blockquote',
    title: 'Quote',
    subtext: 'Insert a blockquote',
    group: 'Blocks',
    aliases: ['quote', 'blockquote', 'citaat'],
    emoji: '"',
  },
  {
    key: 'bullet_list',
    title: 'Bullet List',
    subtext: 'Unordered list',
    group: 'Blocks',
    aliases: ['bullet', 'list', 'ul', 'lijst'],
    emoji: '-',
  },
  {
    key: 'numbered_list',
    title: 'Numbered List',
    subtext: 'Ordered list',
    group: 'Blocks',
    aliases: ['numbered', 'ol', 'genummerd'],
    emoji: '1.',
  },
  {
    key: 'check_list',
    title: 'Checklist',
    subtext: 'Task list with checkboxes',
    group: 'Blocks',
    aliases: ['check', 'todo', 'checklist', 'taak'],
    emoji: 'V',
  },
];

// ---- Media commands --------------------------------------------------------

export const mediaCommands: SlashCommand[] = [
  {
    key: 'image',
    title: 'Image',
    subtext: 'Upload or embed an image',
    group: 'Media',
    aliases: ['image', 'img', 'photo', 'afbeelding', 'foto'],
    emoji: 'P',
  },
  {
    key: 'video',
    title: 'Video',
    subtext: 'Upload a video (AI-generated videos get EU AI label)',
    group: 'Media',
    aliases: ['video', 'mp4', 'film', 'filmpje'],
    emoji: '\u{25B6}',
  },
  {
    key: 'ai_video',
    title: 'AI Video',
    subtext: 'Generate a video with AI (EU AI Act labelled)',
    group: 'AI Media',
    aliases: ['ai video', 'ai-video', 'generate video', 'gen video', 'ai film'],
    emoji: '\u{1F3AC}',
    badge: 'AI',
  },
];

// ---- Agent commands --------------------------------------------------------

export const agentCommands: SlashCommand[] = [
  {
    key: 'agent_verhaal',
    title: 'Verhaal',
    subtext: 'Open story agent',
    group: 'Agents',
    aliases: ['verhaal', 'story', 'memoir'],
    emoji: '\u{1F4D6}',
    badge: 'Agent',
  },
  {
    key: 'agent_sove',
    title: 'Sove',
    subtext: 'Open Sove assistant',
    group: 'Agents',
    aliases: ['sove'],
    emoji: '\u{1F916}',
    badge: 'Agent',
  },
  {
    key: 'agent_kokkin',
    title: 'Kokkin',
    subtext: 'Open recipe agent',
    group: 'Agents',
    aliases: ['kokkin', 'recipe', 'recept'],
    emoji: '\u{1F373}',
    badge: 'Agent',
  },
  {
    key: 'agent_wetenschap',
    title: 'Wetenschap',
    subtext: 'Open science agent',
    group: 'Agents',
    aliases: ['wetenschap', 'science'],
    emoji: '\u{1F52C}',
    badge: 'Agent',
  },
  {
    key: 'agent_kaart',
    title: 'Kaart',
    subtext: 'Open map agent',
    group: 'Agents',
    aliases: ['kaart', 'map'],
    emoji: '\u{1F5FA}',
    badge: 'Agent',
  },
  {
    key: 'agent_ziel',
    title: 'Ziel',
    subtext: 'Open soul agent',
    group: 'Agents',
    aliases: ['ziel', 'soul'],
    emoji: '\u{1F4AB}',
    badge: 'Agent',
  },
];

// ---- AI commands -----------------------------------------------------------

export const aiCommands: SlashCommand[] = [
  {
    key: 'ai_rewrite',
    title: 'Rewrite',
    subtext: 'Rewrite selected text with AI',
    group: 'AI',
    aliases: ['rewrite', 'herschrijf'],
    emoji: 'R',
    badge: 'AI',
  },
  {
    key: 'ai_expand',
    title: 'Expand',
    subtext: 'Expand bullet to paragraph',
    group: 'AI',
    aliases: ['expand', 'uitbreiden'],
    emoji: 'E',
    badge: 'AI',
  },
  {
    key: 'ai_summarise',
    title: 'Summarise',
    subtext: 'Condense block to summary',
    group: 'AI',
    aliases: ['summarise', 'summarize', 'samenvatten'],
    emoji: 'S',
    badge: 'AI',
  },
  {
    key: 'ai_translate',
    title: 'Translate',
    subtext: 'Toggle NL / EN',
    group: 'AI',
    aliases: ['translate', 'vertaal', 'vertalen'],
    emoji: 'T',
    badge: 'AI',
  },
];

// ---- StoryKeeper commands --------------------------------------------------

export const storyKeeperCommands: SlashCommand[] = [
  {
    key: 'sk_beat',
    title: 'Beat',
    subtext: 'Save paragraph as SK beat',
    group: 'StoryKeeper',
    aliases: ['beat'],
    emoji: 'B',
    badge: 'SK',
  },
  {
    key: 'frontmatter',
    title: 'Frontmatter',
    subtext: 'Insert YAML metadata block',
    group: 'StoryKeeper',
    aliases: ['frontmatter', 'yaml', 'metadata', 'meta'],
    emoji: '---',
    badge: 'SK',
  },
];

// ---- All commands ----------------------------------------------------------

export const allCommands: SlashCommand[] = [
  ...blockCommands,
  ...mediaCommands,
  ...agentCommands,
  ...aiCommands,
  ...storyKeeperCommands,
];

// ---- Context-aware ranking -------------------------------------------------

export type DocType = 'memoir' | 'recipe' | 'science' | 'map' | 'soul' | 'default';

/** Map doc types to the agent key that should rank first. */
export const docTypeAgentPriority: Record<DocType, string | null> = {
  memoir: 'agent_verhaal',
  recipe: 'agent_kokkin',
  science: 'agent_wetenschap',
  map: 'agent_kaart',
  soul: 'agent_ziel',
  default: null,
};
